// ANGEL FLIGHT — ThermalEffect: the fused FLIR fragment pass.
//
// One custom postprocessing Effect that turns the rendered frame into a thermal
// gun-camera image: luminance → contrast → sensor blow-out clip → heat LUT
// (white-hot / black-hot / ironbow) → grain + fixed-pattern streak + scanlines +
// vignette + edge chromatic aberration. Fusing it into a single mainImage keeps
// the whole sensor stack to one draw (mobile fill-rate). Bloom stays separate
// (it needs its own downsample chain) and runs before this in the composer.

import { BlendFunction, Effect } from 'postprocessing';
import * as THREE from 'three';
import { THERMAL } from '../config/thermal';

const FRAG = /* glsl */ `
uniform float uTime;
uniform float uContrast;
uniform float uClip;
uniform float uBlackHot;     // 0 = white-hot, 1 = black-hot
uniform float uIronbow;      // 0 = grayscale, 1 = ironbow palette
uniform float uGrain;
uniform float uFixedPattern;
uniform float uScanline;
uniform float uScanCount;
uniform float uVignette;
uniform float uAberration;

// Hash noise for animated grain.
float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Ironbow-ish ramp: cold→blue→magenta→orange→yellow→white.
vec3 ironbow(float t) {
  vec3 c0 = vec3(0.0, 0.0, 0.10);
  vec3 c1 = vec3(0.20, 0.0, 0.45);
  vec3 c2 = vec3(0.75, 0.15, 0.35);
  vec3 c3 = vec3(1.0, 0.55, 0.0);
  vec3 c4 = vec3(1.0, 0.95, 0.55);
  vec3 c5 = vec3(1.0, 1.0, 1.0);
  if (t < 0.2) return mix(c0, c1, t / 0.2);
  if (t < 0.4) return mix(c1, c2, (t - 0.2) / 0.2);
  if (t < 0.6) return mix(c2, c3, (t - 0.4) / 0.2);
  if (t < 0.8) return mix(c3, c4, (t - 0.6) / 0.2);
  return mix(c4, c5, (t - 0.8) / 0.2);
}

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  // Radial offset that grows toward the frame edges → a cheap one-sample
  // chromatic fringe applied to the final heat colour (sensor/optic drift).
  vec2 center = uv - 0.5;
  float edge = dot(center, center) * 4.0;
  vec2 dir = center * uAberration * edge;

  // Luminance == temperature (the scene authors emissive heat directly).
  float lum = dot(inputColor.rgb, vec3(0.299, 0.587, 0.114));

  // Contrast curve deepens the cold darks, then sensor blow-out clamps the hot
  // tail to pure white (the #1 FLIR tell).
  lum = pow(clamp(lum, 0.0, 1.0), uContrast);
  if (lum >= uClip) lum = 1.0;

  // Polarity toggle.
  float t = mix(lum, 1.0 - lum, uBlackHot);

  // Heat LUT: grayscale white-hot, or ironbow.
  vec3 heat = mix(vec3(t), ironbow(t), uIronbow);

  // Animated grain + a faint vertical fixed-pattern streak (uncooled sensor).
  float g = (hash(uv * 800.0 + uTime) - 0.5) * uGrain;
  float fp = (hash(vec2(uv.x * 400.0, 0.0)) - 0.5) * uFixedPattern;
  heat += g + fp;

  // Horizontal interlace scanlines.
  float sl = 1.0 - uScanline * (0.5 + 0.5 * sin(uv.y * uScanCount * 3.14159));
  heat *= sl;

  // Radial vignette.
  float vig = 1.0 - uVignette * smoothstep(0.25, 0.85, length(center));
  heat *= vig;

  // Tiny CA tint at the edges so hot blobs fringe slightly (cheap, one-sample).
  heat.r += dir.x * 6.0;
  heat.b -= dir.x * 6.0;

  outputColor = vec4(clamp(heat, 0.0, 1.0), inputColor.a);
}
`;

export class ThermalEffect extends Effect {
  private readonly time: THREE.Uniform<number>;

  constructor() {
    const f = THERMAL.flir;
    const s = THERMAL.sensor;
    const uniforms = new Map<string, THREE.Uniform>([
      ['uTime', new THREE.Uniform(0)],
      ['uContrast', new THREE.Uniform(f.contrast)],
      ['uClip', new THREE.Uniform(f.clipThreshold)],
      ['uBlackHot', new THREE.Uniform(f.blackHot ? 1 : 0)],
      ['uIronbow', new THREE.Uniform(f.palette === 'ironbow' ? 1 : 0)],
      ['uGrain', new THREE.Uniform(s.grain)],
      ['uFixedPattern', new THREE.Uniform(s.fixedPattern)],
      ['uScanline', new THREE.Uniform(s.scanline)],
      ['uScanCount', new THREE.Uniform(s.scanlineCount)],
      ['uVignette', new THREE.Uniform(s.vignette)],
      ['uAberration', new THREE.Uniform(s.chromaticAberration)],
    ]);

    super('ThermalEffect', FRAG, {
      blendFunction: BlendFunction.SRC, // fully replace — this IS the image
      uniforms,
    });

    this.time = uniforms.get('uTime') as THREE.Uniform<number>;
  }

  /** Advance animated grain; called by the pipeline each frame. */
  update(_renderer: unknown, _input: unknown, deltaTime: number): void {
    this.time.value += deltaTime;
  }

  setPolarity(blackHot: boolean): void {
    (this.uniforms.get('uBlackHot') as THREE.Uniform<number>).value = blackHot ? 1 : 0;
  }

  setPalette(palette: 'whiteHot' | 'ironbow'): void {
    (this.uniforms.get('uIronbow') as THREE.Uniform<number>).value = palette === 'ironbow' ? 1 : 0;
  }
}
